import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { headerContent } from "@/app/content/uiContent";
import { ARIA_LABELS, NAVIGATION } from "@/config/constants";

const logoUrl = "/images/logo.svg";

const NAV_LINK_CLASS =
  "text-slate-900 hover:text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 transition-colors font-medium text-base";
const NAV_LINK_ACTIVE_CLASS =
  "text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 font-medium text-base";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const isBlog = location.pathname.startsWith('/blog');

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    menuToggleRef.current?.focus();
  };

  // ESC + focus trap
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const elements = [
      ...Array.from(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
      menuToggleRef.current,
    ].filter((el): el is HTMLElement => el !== null);
    const first = elements[0];
    const last = elements[elements.length - 1];

    // Przenieś fokus na pierwsze pole w menu po otwarciu
    first?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        menuToggleRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || elements.length === 0) return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileMenuOpen]);

  // Zablokuj scroll strony gdy menu jest otwarte
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <>
    {/* Backdrop — kliknięcie poza menu zamyka je */}
    {mobileMenuOpen && (
      <div
        className="fixed inset-0 z-40 bg-black/30 lg:hidden"
        aria-hidden="true"
        onClick={closeMobileMenu}
      />
    )}
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4 xl:py-5">
        <nav aria-label={ARIA_LABELS.mainNav} className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a
              href="/"
              className="focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md inline-block"
              aria-label={ARIA_LABELS.homeLink}
            >
              <img
                src={logoUrl}
                alt="Bastun"
                width={160}
                height={32}
                decoding="async"
                className="h-8 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center gap-8">
            <li>
              <a href={`/#${NAVIGATION.problem.id}`} className={NAV_LINK_CLASS}>
                {NAVIGATION.problem.label}
              </a>
            </li>
            <li>
              <a href={`/#${NAVIGATION.solution.id}`} className={NAV_LINK_CLASS}>
                {NAVIGATION.solution.label}
              </a>
            </li>
            <li>
              <a href={`/#${NAVIGATION.services.id}`} className={NAV_LINK_CLASS}>
                {NAVIGATION.services.label}
              </a>
            </li>
            <li>
              <Link
                to={NAVIGATION.blog.href}
                className={isBlog ? NAV_LINK_ACTIVE_CLASS : NAV_LINK_CLASS}
                aria-current={isBlog ? 'page' : undefined}
              >
                {NAVIGATION.blog.label}
              </Link>
            </li>
            <li>
              <a href={`/#${NAVIGATION.contact.id}`} className={NAV_LINK_CLASS}>
                {NAVIGATION.contact.label}
              </a>
            </li>
          </ul>

          {/* CTA Button */}
          <a
            href={`/#${NAVIGATION.contact.id}`}
            className="hidden lg:inline-flex items-center gap-3 bg-[#004f4f] text-white px-6 py-3 rounded-full hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 transition-colors font-bold text-base"
          >
            {headerContent.ctaDesktopLabel}
          </a>

          {/* Mobile Menu Button */}
          <button
            ref={menuToggleRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-[#004f4f] p-2 focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md"
            aria-label={mobileMenuOpen ? ARIA_LABELS.closeMenu : ARIA_LABELS.openMenu}
            aria-expanded={mobileMenuOpen}
            aria-haspopup="true"
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav
            ref={menuRef}
            id="mobile-menu"
            aria-label={headerContent.mobileMenuAriaLabel}
            className="lg:hidden mt-6 pb-4 border-t border-gray-100 pt-6"
          >
            <ul className="flex flex-col gap-4">
              <li>
                <a href={`/#${NAVIGATION.problem.id}`} className="block text-slate-900 hover:text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 transition-colors font-medium text-base" onClick={closeMobileMenu}>
                  {NAVIGATION.problem.label}
                </a>
              </li>
              <li>
                <a href={`/#${NAVIGATION.solution.id}`} className="block text-slate-900 hover:text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 transition-colors font-medium text-base" onClick={closeMobileMenu}>
                  {NAVIGATION.solution.label}
                </a>
              </li>
              <li>
                <a href={`/#${NAVIGATION.services.id}`} className="block text-slate-900 hover:text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 transition-colors font-medium text-base" onClick={closeMobileMenu}>
                  {NAVIGATION.services.label}
                </a>
              </li>
              <li>
                <Link to={NAVIGATION.blog.href} className={`block px-3 py-2 focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md font-medium text-base transition-colors ${isBlog ? 'text-[#004f4f]' : 'text-slate-900 hover:text-[#004f4f]'}`} onClick={closeMobileMenu} aria-current={isBlog ? 'page' : undefined}>
                  {NAVIGATION.blog.label}
                </Link>
              </li>
              <li>
                <a href={`/#${NAVIGATION.contact.id}`} className="block text-slate-900 hover:text-[#004f4f] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md px-3 py-2 transition-colors font-medium text-base" onClick={closeMobileMenu}>
                  {NAVIGATION.contact.label}
                </a>
              </li>
              <li className="pt-4">
                <a href={`/#${NAVIGATION.contact.id}`} className="block bg-[#004f4f] text-white px-6 py-3 rounded-full hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 transition-colors font-bold text-center text-base" onClick={closeMobileMenu}>
                  {headerContent.ctaMobileLabel}
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
    </>
  );
}
